import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast, Toaster } from "sonner";
import ChatComposer from "@/components/chat/ChatComposer";
import ChatMessage from "@/components/chat/ChatMessage";
import ThreadPanel from "@/components/chat/ThreadPanel";
import ChannelManager from "@/components/chat/ChannelManager";
import MembersModal from "@/components/chat/MembersModal";
import {
  Hash, ShieldCheck, User, Ticket, Plus, X, MessageSquare, Loader2, CheckCircle2,
  Lock, Settings2, Search, Pin, Megaphone, Send, ArrowLeft, Users, Video, CheckCheck, ExternalLink, Check,
} from "lucide-react";

const CATEGORIES = [
  { value: "back_office", label: "Back-office" },
  { value: "denonciation", label: "Dénonciation" },
  { value: "option", label: "Option" },
  { value: "plan_media", label: "Plan média" },
  { value: "litige_client", label: "Litige client" },
  { value: "nego", label: "Négo" },
  { value: "autres", label: "Autres" },
];
const CAT_LABEL = Object.fromEntries(CATEGORIES.map((c) => [c.value, c.label]));
const CAT_COLOR = {
  back_office: "bg-slate-100 text-slate-700 border-slate-300",
  denonciation: "bg-red-50 text-red-700 border-red-200",
  option: "bg-purple-50 text-purple-700 border-purple-200",
  plan_media: "bg-sky-50 text-sky-700 border-sky-200",
  litige_client: "bg-amber-50 text-amber-700 border-amber-200",
  nego: "bg-teal-50 text-teal-700 border-teal-200",
  autres: "bg-stone-100 text-stone-600 border-stone-300",
};
const STATUS = [
  { value: "ouvert", label: "Ouvert", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { value: "en_cours", label: "En cours", color: "bg-amber-50 text-amber-700 border-amber-200" },
  { value: "resolu", label: "Résolu", color: "bg-slate-100 text-slate-500 border-slate-300" },
];
const STATUS_LABEL = Object.fromEntries(STATUS.map((s) => [s.value, s]));

const SECTIONS = [
  { key: "general", label: "Discussions générales" },
  { key: "admin", label: "Administration" },
  { key: "prive", label: "Salons privés" },
];

function chanIcon(c) {
  if (c.section === "admin") return ShieldCheck;
  if (c.type === "dm") return User;
  if (c.section === "prive") return Users;
  if (c.post_access === "admin") return Lock;
  return Hash;
}

const fmtTime = (iso) => { try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

export default function Chat() {
  const { user } = useAuth();
  const isAdmin = ["admin", "dirigeant"].includes(user?.role);
  const [params, setParams] = useSearchParams();
  const [channels, setChannels] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [members, setMembers] = useState([]);
  const [ticket, setTicket] = useState(null);
  const [staff, setStaff] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [showManager, setShowManager] = useState(false);
  const [threadId, setThreadId] = useState(null);
  const [pins, setPins] = useState([]);
  const [showPins, setShowPins] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [searchRes, setSearchRes] = useState([]);
  const [annOpen, setAnnOpen] = useState(false);
  const [membersFor, setMembersFor] = useState(null);
  const [videoModal, setVideoModal] = useState(false);
  const [toTicketMsg, setToTicketMsg] = useState(null);
  const [receiptsMsg, setReceiptsMsg] = useState(null);
  const scrollRef = useRef(null);

  const isTicket = activeId && activeId.startsWith("ticket:");
  const activeChannel = channels.find((c) => c.id === activeId);
  const canPost = isTicket ? (ticket?.status !== "resolu") : (activeChannel ? activeChannel.can_post : true);
  const showConvo = !!activeId || searchOpen;

  const loadChannels = useCallback(() => api.get("/chat/channels").then((r) => setChannels(r.data)).catch(() => {}), []);
  const loadTickets = useCallback(() => {
    const q = statusFilter ? `?status=${statusFilter}` : "";
    return api.get(`/chat/tickets${q}`).then((r) => setTickets(r.data)).catch(() => {});
  }, [statusFilter]);
  const loadMessages = useCallback((id) => {
    if (!id) return Promise.resolve();
    return api.get(`/chat/channels/${id}/messages`).then((r) => setMessages(r.data)).catch(() => {});
  }, []);
  const loadPins = useCallback((id) => {
    if (!id) return;
    api.get(`/chat/channels/${id}/pins`).then((r) => setPins(r.data)).catch(() => setPins([]));
  }, []);

  useEffect(() => { loadChannels(); if (isAdmin) api.get("/users").then((r) => setStaff(r.data)).catch(() => {}); }, [loadChannels, isAdmin]);
  useEffect(() => { loadTickets(); }, [loadTickets]);

  useEffect(() => {
    const c = params.get("c");
    if (c && !activeId) setActiveId(c);
    else if (!activeId && channels.length && window.innerWidth >= 1024) setActiveId(channels[0].id);
  }, [params, channels, activeId]);

  useEffect(() => {
    if (!activeId) return;
    setThreadId(null); setShowPins(false);
    loadMessages(activeId);
    loadPins(activeId);
    api.get(`/chat/channels/${activeId}/members`).then((r) => setMembers(r.data)).catch(() => setMembers([]));
    if (activeId.startsWith("ticket:")) {
      api.get(`/chat/tickets/${activeId.slice(7)}`).then((r) => setTicket(r.data)).catch(() => setTicket(null));
    } else { setTicket(null); }
  }, [activeId, loadMessages, loadPins]);

  useEffect(() => {
    if (!activeId) return;
    const t = setInterval(() => { loadMessages(activeId); loadChannels(); loadTickets(); }, 6000);
    return () => clearInterval(t);
  }, [activeId, loadMessages, loadChannels, loadTickets]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const openChannel = (id) => { setActiveId(id); setParams({ c: id }); setSearchOpen(false); };

  const sendMessage = async ({ body, mentions, attachments }) => {
    try {
      await api.post(`/chat/channels/${activeId}/messages`, { body, mentions, attachments });
      await loadMessages(activeId);
      loadChannels(); loadTickets();
    } catch (e) { toast.error(e?.response?.data?.detail || "Envoi impossible"); }
  };

  const refreshActive = () => { loadMessages(activeId); loadPins(activeId); };
  const react = async (m, emoji) => { try { await api.post(`/chat/messages/${m.id}/react`, { emoji }); refreshActive(); } catch {} };
  const pin = async (m) => { try { await api.post(`/chat/messages/${m.id}/pin`); refreshActive(); toast.success("Message épinglé"); } catch {} };
  const unpin = async (m) => { try { await api.delete(`/chat/messages/${m.id}/pin`); refreshActive(); } catch {} };
  const editMsg = async (m, body) => { try { await api.patch(`/chat/messages/${m.id}`, { body }); refreshActive(); } catch (e) { toast.error("Modification impossible"); } };
  const delMsg = async (m) => { if (!window.confirm("Supprimer ce message ?")) return; try { await api.delete(`/chat/messages/${m.id}`); refreshActive(); } catch {} };

  const updateTicket = async (patch) => {
    try {
      const r = await api.patch(`/chat/tickets/${activeId.slice(7)}`, patch);
      setTicket(r.data); loadTickets();
      toast.success("Ticket mis à jour");
    } catch (e) { toast.error(e?.response?.data?.detail || "Mise à jour impossible"); }
  };

  const runSearch = async (q) => {
    setSearchQ(q);
    if (q.trim().length < 2) { setSearchRes([]); return; }
    try { const r = await api.get(`/chat/search?q=${encodeURIComponent(q.trim())}`); setSearchRes(r.data); } catch {}
  };

  const saveVideo = async (url) => {
    try {
      await api.put(`/chat/channels/${activeId}/video`, { video_url: url });
      await loadChannels();
      setVideoModal(false);
      toast.success(url ? "Lien visio enregistré" : "Lien visio retiré");
    } catch (e) { toast.error(e?.response?.data?.detail || "Enregistrement impossible"); }
  };

  const headerTitle = isTicket ? (ticket?.title || "Ticket") : (activeChannel?.name || "");
  const grouped = useMemo(() => {
    const out = {};
    for (const s of SECTIONS) out[s.key] = channels.filter((c) => c.section === s.key);
    return out;
  }, [channels]);

  return (
    <div className="flex h-full" data-testid="chat-page">
      <Toaster position="top-center" richColors />

      {/* Rail */}
      <div className={`w-full lg:w-72 border-r border-slate-200 bg-white flex-col shrink-0 ${showConvo ? "hidden lg:flex" : "flex"}`}>
        <div className="px-4 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="min-w-0">
            <h1 className="text-lg font-bold text-[#111111]" style={{ fontFamily: "Cabinet Grotesk" }}>Chat</h1>
            <p className="text-[11px] text-slate-400">Salons & tickets de l'équipe</p>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setSearchOpen((v) => !v)} data-testid="chat-search-toggle" title="Rechercher" className="p-1.5 text-slate-500 hover:text-[#B8873A]"><Search className="w-4 h-4" /></button>
            {isAdmin && <button onClick={() => setShowManager(true)} data-testid="chat-manage-btn" title="Gérer les salons" className="p-1.5 text-slate-500 hover:text-[#B8873A]"><Settings2 className="w-4 h-4" /></button>}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto py-3">
          {SECTIONS.map((s) => (grouped[s.key] && grouped[s.key].length > 0) && (
            <div key={s.key} className="mb-3">
              <div className="px-4 text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold mb-1">{s.label}</div>
              {grouped[s.key].map((c) => {
                const Icon = chanIcon(c);
                const active = c.id === activeId;
                return (
                  <button key={c.id} onClick={() => openChannel(c.id)} data-testid={`chat-channel-${c.id}`}
                    className={`w-full flex items-center gap-2 px-4 py-2 text-sm transition-colors ${active ? "bg-slate-100 text-[#111111] font-semibold" : "text-slate-600 hover:bg-slate-50"}`}>
                    <Icon className="w-4 h-4 shrink-0" strokeWidth={1.75} />
                    <span className="truncate flex-1 text-left">{c.name}</span>
                    {c.unread > 0 && <span className="shrink-0 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid={`chat-unread-${c.id}`}>{c.unread}</span>}
                  </button>
                );
              })}
            </div>
          ))}

          <div className="px-4 mt-2 mb-1 flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">Tickets</span>
            <button onClick={() => setShowNew(true)} data-testid="new-ticket-btn" className="text-[#B8873A] hover:text-[#111111]"><Plus className="w-4 h-4" /></button>
          </div>
          <div className="px-4 mb-2">
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="ticket-status-filter"
              className="w-full text-xs border border-slate-200 rounded-sm px-2 py-1 bg-white">
              <option value="">Tous les statuts</option>
              {STATUS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          {tickets.length === 0 && <div className="px-4 py-3 text-xs text-slate-400">Aucun ticket</div>}
          {tickets.map((t) => {
            const cid = `ticket:${t.id}`;
            const active = cid === activeId;
            return (
              <button key={t.id} onClick={() => openChannel(cid)} data-testid={`chat-ticket-${t.id}`}
                className={`w-full px-4 py-2 text-left transition-colors ${active ? "bg-slate-100" : "hover:bg-slate-50"}`}>
                <div className="flex items-center gap-2">
                  <Ticket className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                  <span className={`truncate flex-1 text-sm ${active ? "font-semibold text-[#111111]" : "text-slate-700"}`}>{t.title}</span>
                  {t.unread > 0 && <span className="shrink-0 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">{t.unread}</span>}
                </div>
                <div className="flex items-center gap-1.5 mt-1 ml-5">
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-full border ${CAT_COLOR[t.category]}`}>{CAT_LABEL[t.category]}</span>
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-full border ${STATUS_LABEL[t.status]?.color}`}>{STATUS_LABEL[t.status]?.label}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Center */}
      <div className={`flex-1 flex-col min-w-0 bg-[#F8F9FA] ${showConvo ? "flex" : "hidden lg:flex"}`}>
        {searchOpen ? (
          <div className="flex-1 flex flex-col">
            <div className="px-4 py-3.5 border-b border-slate-200 bg-white flex items-center gap-3">
              <button onClick={() => { setSearchOpen(false); setSearchQ(""); setSearchRes([]); }} data-testid="chat-search-back" className="lg:hidden -ml-1 p-1 text-slate-500 hover:text-[#111111]"><ArrowLeft className="w-5 h-5" /></button>
              <Search className="w-4 h-4 text-slate-400" />
              <input autoFocus value={searchQ} onChange={(e) => runSearch(e.target.value)} data-testid="chat-search-input"
                placeholder="Rechercher un message…" className="flex-1 text-sm focus:outline-none" />
              <button onClick={() => { setSearchOpen(false); setSearchQ(""); setSearchRes([]); }} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 lg:px-6 py-4 space-y-2" data-testid="chat-search-results">
              {searchQ.trim().length < 2 && <div className="text-sm text-slate-400 text-center py-10">Tapez au moins 2 caractères.</div>}
              {searchQ.trim().length >= 2 && searchRes.length === 0 && <div className="text-sm text-slate-400 text-center py-10">Aucun résultat.</div>}
              {searchRes.map((m) => (
                <button key={m.id} onClick={() => openChannel(m.channel_id)} data-testid={`search-result-${m.id}`}
                  className="w-full text-left bg-white border border-slate-200 rounded-sm px-4 py-2.5 hover:border-[#B8873A] transition-colors">
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-xs font-semibold text-[#B8873A]">{m.channel_label}</span>
                    <span className="text-[10px] text-slate-400">{fmtTime(m.created_at)}</span>
                  </div>
                  <div className="text-xs text-slate-500">{m.author_name}</div>
                  <div className="text-sm text-slate-800 line-clamp-2">{m.body}</div>
                </button>
              ))}
            </div>
          </div>
        ) : !activeId ? (
          <div className="flex-1 flex items-center justify-center text-slate-400">
            <div className="text-center"><MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-40" /><p className="text-sm">Sélectionne un salon ou un ticket</p></div>
          </div>
        ) : (
          <>
            <div className="px-4 lg:px-6 py-3 border-b border-slate-200 bg-white flex items-center gap-3 flex-wrap">
              <button onClick={() => { setActiveId(null); setParams({}); }} data-testid="chat-back-btn" className="lg:hidden -ml-1 p-1 text-slate-500 hover:text-[#111111]"><ArrowLeft className="w-5 h-5" /></button>
              <div className="min-w-0">
                <div className="font-semibold text-[#111111] truncate flex items-center gap-2">
                  {isTicket && <Ticket className="w-4 h-4 text-[#B8873A]" />}
                  {!isTicket && activeChannel?.post_access === "admin" && <Lock className="w-3.5 h-3.5 text-slate-400" />}
                  {headerTitle}
                </div>
                {isTicket && ticket && <div className="text-[11px] text-slate-400">Ouvert par {ticket.created_by_name} · {CAT_LABEL[ticket.category]}</div>}
                {!isTicket && activeChannel?.post_access === "admin" && <div className="text-[11px] text-slate-400">Lecture seule — seuls les administrateurs écrivent</div>}
              </div>

              <div className="ml-auto flex items-center gap-2">
                {!isTicket && pins.length > 0 && (
                  <button onClick={() => setShowPins((v) => !v)} data-testid="chat-pins-toggle"
                    className="flex items-center gap-1 text-xs text-slate-600 hover:text-[#B8873A] border border-slate-200 rounded-sm px-2 py-1.5">
                    <Pin className="w-3.5 h-3.5" /> {pins.length}
                  </button>
                )}
                {!isTicket && isAdmin && (activeChannel?.type === "dm" || activeChannel?.section === "prive") && (
                  <button onClick={() => setMembersFor({ channelId: activeId, title: headerTitle, lockedId: activeId.startsWith("dm:") ? activeId.slice(3) : null })}
                    data-testid="chat-members-btn" title="Gérer les accès"
                    className="flex items-center gap-1 text-xs text-slate-600 hover:text-[#B8873A] border border-slate-200 rounded-sm px-2 py-1.5">
                    <Users className="w-3.5 h-3.5" />
                  </button>
                )}
                {!isTicket && isAdmin && (
                  <button onClick={() => setAnnOpen(true)} data-testid="chat-announce-btn" title="Annonce CRM"
                    className="flex items-center gap-1 text-xs text-slate-600 hover:text-[#B8873A] border border-slate-200 rounded-sm px-2 py-1.5">
                    <Megaphone className="w-3.5 h-3.5" />
                  </button>
                )}
                {isTicket && ticket && isAdmin && (
                  <select value={ticket.assignee_id || ""} onChange={(e) => updateTicket({ assignee_id: e.target.value })} data-testid="ticket-assignee-select"
                    className="text-xs border border-slate-300 rounded-sm px-2 py-1.5 bg-white max-w-[150px]">
                    <option value="">Non assigné</option>
                    {staff.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                )}
                {isTicket && ticket && !isAdmin && ticket.assignee_name && <span className="text-xs text-slate-500">Assigné à {ticket.assignee_name}</span>}
                {isTicket && ticket && (
                  <select value={ticket.status} onChange={(e) => updateTicket({ status: e.target.value })} data-testid="ticket-status-select"
                    className={`text-xs font-semibold border rounded-sm px-2 py-1.5 ${STATUS_LABEL[ticket.status]?.color}`}>
                    {STATUS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                )}
              </div>
            </div>

            {!isTicket && activeChannel?.type === "channel" && (activeChannel.video_url || canPost) && (
              <div className="bg-[#111111] text-white flex items-center gap-3 px-4 lg:px-6 py-2" data-testid="chat-video-bar">
                <Video className="w-4 h-4 text-[#E0B050] shrink-0" />
                {activeChannel.video_url ? (
                  <>
                    <a href={activeChannel.video_url} target="_blank" rel="noreferrer" data-testid="chat-video-join"
                      className="inline-flex items-center gap-1.5 bg-[#E0B050] text-[#111111] text-xs font-bold px-3 py-1.5 rounded-sm hover:bg-[#d4a645] transition-colors">
                      <ExternalLink className="w-3.5 h-3.5" /> Rejoindre la visio
                    </a>
                    <span className="text-[11px] text-slate-300 truncate flex-1 min-w-0 hidden sm:block">{activeChannel.video_url}</span>
                    {canPost && <button onClick={() => setVideoModal(true)} data-testid="chat-video-edit" className="text-[11px] text-slate-300 hover:text-white underline shrink-0">modifier</button>}
                  </>
                ) : (
                  <button onClick={() => setVideoModal(true)} data-testid="chat-video-add" className="text-xs text-slate-200 hover:text-white">
                    + Ajouter un lien visio (Google Meet / Zoom)
                  </button>
                )}
              </div>
            )}

            {showPins && pins.length > 0 && (              <div className="bg-amber-50/60 border-b border-amber-100 px-6 py-2 space-y-1.5 max-h-40 overflow-y-auto" data-testid="chat-pins-panel">
                {pins.map((p) => (
                  <div key={p.id} className="flex items-start gap-2 text-xs">
                    <Pin className="w-3 h-3 text-[#B8873A] mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <span className="font-semibold text-slate-700">{p.author_name}: </span>
                      <span className="text-slate-600">{p.body || "Pièce jointe"}</span>
                    </div>
                    <button onClick={() => unpin(p)} className="ml-auto text-slate-400 hover:text-red-500 shrink-0"><X className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            )}

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 lg:px-6 py-4 space-y-3" data-testid="chat-messages">
              {messages.length === 0 && <div className="text-center text-sm text-slate-400 py-10">Aucun message. Lance la conversation.</div>}
              {messages.map((m) => (
                <ChatMessage key={m.id} m={m} user={user} isAdmin={isAdmin} members={members} canPost={canPost}
                  onReact={react} onPin={pin} onUnpin={unpin} onEdit={editMsg} onDelete={delMsg}
                  onToTicket={!isTicket ? (msg) => setToTicketMsg(msg) : undefined}
                  onReceipts={(msg) => setReceiptsMsg(msg)}
                  onOpenThread={(msg) => setThreadId(msg.id)} />
              ))}
            </div>

            {canPost ? (
              <ChatComposer members={members} onSend={sendMessage} />
            ) : (
              <div className="border-t border-slate-200 bg-white px-6 py-3 text-center text-xs text-slate-400" data-testid="chat-readonly-notice">
                {isTicket ? "Ticket résolu — réouvrez-le pour écrire." : "Salon en lecture seule — seuls les administrateurs peuvent écrire."}
              </div>
            )}
          </>
        )}
      </div>

      {/* Thread drawer */}
      {threadId && activeId && (
        <ThreadPanel channelId={activeId} parentId={threadId} user={user} isAdmin={isAdmin} members={members}
          canPost={canPost} onClose={() => setThreadId(null)} onChanged={refreshActive} />
      )}

      {showNew && (
        <NewTicketModal isAdmin={isAdmin} staff={staff} onClose={() => setShowNew(false)}
          onCreated={(tid) => { setShowNew(false); loadTickets(); openChannel(`ticket:${tid}`); }} />
      )}
      {showManager && (
        <ChannelManager channels={channels} staff={staff} onClose={() => setShowManager(false)} onChanged={loadChannels} />
      )}
      {membersFor && (
        <MembersModal channelId={membersFor.channelId} title={membersFor.title} lockedId={membersFor.lockedId}
          staff={staff} onClose={() => setMembersFor(null)} onSaved={loadChannels} />
      )}
      {annOpen && (
        <AnnouncementModal onClose={() => setAnnOpen(false)} />
      )}
      {videoModal && activeChannel && (
        <VideoLinkModal current={activeChannel.video_url || ""} channelName={activeChannel.name}
          onSave={saveVideo} onClose={() => setVideoModal(false)} />
      )}
      {toTicketMsg && (
        <ToTicketModal msg={toTicketMsg} isAdmin={isAdmin} staff={staff}
          onClose={() => setToTicketMsg(null)}
          onCreated={(tid) => { setToTicketMsg(null); loadTickets(); openChannel(`ticket:${tid}`); }} />
      )}
      {receiptsMsg && (
        <ReceiptsModal msg={receiptsMsg} onClose={() => setReceiptsMsg(null)} />
      )}
    </div>
  );
}

function AnnouncementModal({ onClose }) {
  const [message, setMessage] = useState("");
  const [level, setLevel] = useState("info");
  const [saving, setSaving] = useState(false);

  const publish = async () => {
    if (!message.trim()) { toast.error("Écris un message"); return; }
    setSaving(true);
    try { await api.post("/chat/announcement", { message: message.trim(), level }); toast.success("Annonce publiée"); window.dispatchEvent(new Event("announcement-changed")); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Publication impossible"); setSaving(false); }
  };
  const clear = async () => { try { await api.delete("/chat/announcement"); toast.success("Annonce retirée"); window.dispatchEvent(new Event("announcement-changed")); onClose(); } catch {} };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="announcement-modal">
      <div className="bg-white rounded-sm shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100">
          <h2 className="font-semibold text-[#111111] flex items-center gap-2"><Megaphone className="w-4 h-4 text-[#B8873A]" /> Annonce importante</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-xs text-slate-500">Un bandeau s'affichera en haut du CRM pour toute l'équipe.</p>
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} data-testid="announcement-input"
            placeholder="Ex : Réunion générale demain 9h en visio." autoFocus
            className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-[#111111]" />
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-slate-600">Niveau :</span>
            {[{ v: "info", l: "Info" }, { v: "urgent", l: "Urgent" }].map((o) => (
              <button key={o.v} onClick={() => setLevel(o.v)} data-testid={`announcement-level-${o.v}`}
                className={`text-xs px-3 py-1 rounded-sm border ${level === o.v ? (o.v === "urgent" ? "bg-red-600 text-white border-red-600" : "bg-[#B8873A] text-white border-[#B8873A]") : "bg-white text-slate-600 border-slate-200"}`}>{o.l}</button>
            ))}
          </div>
        </div>
        <div className="flex justify-between items-center px-5 py-3.5 border-t border-slate-100">
          <button onClick={clear} data-testid="announcement-clear" className="text-xs font-semibold text-slate-500 hover:text-red-500">Retirer l'annonce active</button>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-slate-600 hover:text-slate-900">Annuler</button>
            <button onClick={publish} disabled={saving} data-testid="announcement-publish"
              className="px-4 py-2 bg-[#111111] text-white rounded-sm text-sm font-semibold hover:bg-slate-800 disabled:opacity-50 flex items-center gap-2">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Publier
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function VideoLinkModal({ current, channelName, onSave, onClose }) {
  const [url, setUrl] = useState(current || "");
  const [saving, setSaving] = useState(false);
  const submit = async () => { setSaving(true); await onSave(url.trim()); setSaving(false); };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="video-link-modal">
      <div className="bg-white rounded-sm shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100">
          <h2 className="font-semibold text-[#111111] flex items-center gap-2"><Video className="w-4 h-4 text-[#B8873A]" /> Lien visio — {channelName}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-xs text-slate-500">Colle un lien Google Meet, Zoom, Teams… Il s'affichera en haut du salon pour rejoindre en un clic.</p>
          <input value={url} onChange={(e) => setUrl(e.target.value)} data-testid="video-link-input" autoFocus
            placeholder="https://meet.google.com/abc-defg-hij"
            className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-[#111111]" />
        </div>
        <div className="flex justify-between items-center px-5 py-3.5 border-t border-slate-100">
          <button onClick={() => { setUrl(""); onSave(""); }} data-testid="video-link-remove" className="text-xs font-semibold text-slate-500 hover:text-red-500">Retirer le lien</button>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-slate-600 hover:text-slate-900">Annuler</button>
            <button onClick={submit} disabled={saving} data-testid="video-link-save"
              className="px-4 py-2 bg-[#111111] text-white rounded-sm text-sm font-semibold hover:bg-slate-800 disabled:opacity-50 flex items-center gap-2">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Enregistrer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ToTicketModal({ msg, isAdmin, staff, onClose, onCreated }) {
  const excerpt = (msg.body || "").trim().slice(0, 80);
  const [title, setTitle] = useState(excerpt || "Message du chat");
  const [category, setCategory] = useState("back_office");
  const [assignee, setAssignee] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!title.trim()) { toast.error("Donne un titre au ticket"); return; }
    setSaving(true);
    try {
      const r = await api.post(`/chat/messages/${msg.id}/to-ticket`, { title: title.trim(), category, assignee_id: isAdmin ? (assignee || null) : null });
      toast.success("Ticket créé depuis le message");
      onCreated(r.data.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Conversion impossible"); setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="to-ticket-modal">
      <div className="bg-white rounded-sm shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100">
          <h2 className="font-semibold text-[#111111] flex items-center gap-2"><Ticket className="w-4 h-4 text-[#B8873A]" /> Convertir en ticket</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="bg-slate-50 border border-slate-200 rounded-sm px-3 py-2 text-xs text-slate-600">
            <span className="font-semibold text-slate-700">{msg.author_name} : </span>{msg.body || "Pièce jointe"}
          </div>
          <div>
            <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">Titre</div>
            <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="to-ticket-title" autoFocus
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-[#111111]" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">Sujet</div>
              <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="to-ticket-category"
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white">
                {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            {isAdmin && (
              <div>
                <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">Assigner à</div>
                <select value={assignee} onChange={(e) => setAssignee(e.target.value)} data-testid="to-ticket-assignee"
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white">
                  <option value="">Non assigné</option>
                  {staff.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3.5 border-t border-slate-100">
          <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-slate-600 hover:text-slate-900">Annuler</button>
          <button onClick={submit} disabled={saving} data-testid="to-ticket-submit"
            className="px-4 py-2 bg-[#111111] text-white rounded-sm text-sm font-semibold hover:bg-slate-800 disabled:opacity-50 flex items-center gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ticket className="w-4 h-4" />} Créer le ticket
          </button>
        </div>
      </div>
    </div>
  );
}

function ReceiptsModal({ msg, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/chat/messages/${msg.id}/receipts`).then((r) => setData(r.data)).catch(() => setData({ read: [], unread: [] }));
  }, [msg.id]);
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="receipts-modal">
      <div className="bg-white rounded-sm shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100">
          <h2 className="font-semibold text-[#111111] flex items-center gap-2"><CheckCheck className="w-4 h-4 text-[#B8873A]" /> Accusés de lecture</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 max-h-[60vh] overflow-y-auto">
          {!data ? (
            <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : (
            <div className="space-y-4">
              <div>
                <div className="text-[11px] uppercase tracking-wider font-semibold text-emerald-600 mb-1.5 flex items-center gap-1"><CheckCheck className="w-3.5 h-3.5" /> Lu ({data.read.length})</div>
                {data.read.length === 0 ? <p className="text-xs text-slate-400">Personne pour l'instant.</p> : (
                  <div className="space-y-1" data-testid="receipts-read-list">
                    {data.read.map((u) => <div key={u.id} className="text-sm text-slate-700 flex items-center gap-2"><CheckCheck className="w-3.5 h-3.5 text-emerald-500" /> {u.name}</div>)}
                  </div>
                )}
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-400 mb-1.5">Pas encore lu ({data.unread.length})</div>
                {data.unread.length === 0 ? <p className="text-xs text-slate-400">Tout le monde a lu.</p> : (
                  <div className="space-y-1" data-testid="receipts-unread-list">
                    {data.unread.map((u) => <div key={u.id} className="text-sm text-slate-500 flex items-center gap-2"><Check className="w-3.5 h-3.5 text-slate-300" /> {u.name}</div>)}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function NewTicketModal({ isAdmin, staff, onClose, onCreated }) {  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("back_office");
  const [body, setBody] = useState("");
  const [assignee, setAssignee] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!title.trim()) { toast.error("Donne un titre au ticket"); return; }
    setSaving(true);
    try {
      const r = await api.post("/chat/tickets", { title: title.trim(), category, body: body.trim(), assignee_id: isAdmin ? (assignee || null) : null });
      toast.success("Ticket créé");
      onCreated(r.data.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Création impossible"); setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="new-ticket-modal">
      <div className="bg-white rounded-sm shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100">
          <h2 className="font-semibold text-[#111111]">Nouveau ticket</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">Titre</div>
            <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="ticket-title-input"
              placeholder="Ex : Dénonciation lot B12 — programme Horizon" autoFocus
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-[#111111]" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">Sujet</div>
              <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="ticket-category-select"
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white">
                {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            {isAdmin && (
              <div>
                <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">Assigner à</div>
                <select value={assignee} onChange={(e) => setAssignee(e.target.value)} data-testid="ticket-assignee-new"
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white">
                  <option value="">Non assigné</option>
                  {staff.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
            )}
          </div>
          <div>
            <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">Message</div>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} data-testid="ticket-body-input"
              placeholder="Décris la demande…"
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-[#111111]" />
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3.5 border-t border-slate-100">
          <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-slate-600 hover:text-slate-900">Annuler</button>
          <button onClick={submit} disabled={saving} data-testid="ticket-create-submit"
            className="px-4 py-2 bg-[#111111] text-white rounded-sm text-sm font-semibold hover:bg-slate-800 disabled:opacity-50 flex items-center gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Créer le ticket
          </button>
        </div>
      </div>
    </div>
  );
}
